import { useMemo } from 'react';

import { useNavigate } from 'react-router-dom';

import { Skeleton } from 'antd';
import classNames from 'classnames';

import { useMenuPermissions } from '@/shared/hooks/useMenuPermissions';

import { CONFIGURATION_STEPS } from '@/widgets/admin/AdminConfigurationSection/model/constants';
import { type ConfigurationItem, type ConfigurationStep } from '@/widgets/admin/AdminConfigurationSection/model/types';
import { useAdminConfigurationCounts } from '@/widgets/admin/AdminConfigurationSection/model/useAdminConfigurationCounts';

import styles from './AdminConfigurationSection.module.css';

type AdminConfigurationSectionProps = {
  className?: string;
  title?: string;
  steps?: readonly ConfigurationStep[];
};

const DEFAULT_TITLE = 'Configuration';

export const AdminConfigurationSection = ({
  className,
  title = DEFAULT_TITLE,
  steps = CONFIGURATION_STEPS,
}: AdminConfigurationSectionProps) => {
  const navigate = useNavigate();
  const { hasRoutePermission } = useMenuPermissions();
  const {
    userCount,
    connectorTotalCount,
    dagTotalCount,
    isLoading,
    isUserCountLoading,
    isConnectorCountLoading,
    isDagCountLoading,
  } = useAdminConfigurationCounts();

  const stepsWithDynamicCounts = useMemo(() => {
    return steps.map((step) => {
      return {
        ...step,
        items: step.items.map((item) => {
          const updatedItem = { ...item };

          if (step.label === 'User' && item.label === 'Account') {
            updatedItem.count = userCount;
          }

          if (step.label === 'Ingest' && item.label === 'Departure') {
            updatedItem.count = connectorTotalCount;
          }

          if (step.label === 'Contextualize' && item.label === 'DAG') {
            updatedItem.count = dagTotalCount;
          }

          if (item.path && !item.disabled) {
            updatedItem.disabled = !hasRoutePermission(item.path);
          }

          return updatedItem;
        }),
      };
    });
  }, [steps, userCount, connectorTotalCount, dagTotalCount, hasRoutePermission]);

  const handleItemClick = (item: ConfigurationItem) => {
    if (item.path && !item.disabled) {
      navigate(item.path);
    }
  };

  const getItemCountLoading = (step: ConfigurationStep, item: ConfigurationItem) => {
    if (step.label === 'User' && item.label === 'Account') {
      return isUserCountLoading;
    }

    if (step.label === 'Ingest' && item.label === 'Departure') {
      return isConnectorCountLoading;
    }

    if (step.label === 'Contextualize' && item.label === 'DAG') {
      return isDagCountLoading;
    }

    return false;
  };

  const renderCountValue = (count: number, loading: boolean) => {
    if (loading) {
      return (
        <span className={styles.countSkeletonWrapper} data-testid="configuration-count-skeleton">
          <Skeleton.Input active size="small" style={{ width: 16, minWidth: 16, height: 12 }} />
        </span>
      );
    }

    return count;
  };

  return (
    <section className={`${styles.section} ${className ?? ''}`} aria-label={title}>
      <h2 className={styles.title}>{title}</h2>

      <div className={styles.panel}>
        <ol className={styles.stepTrack} aria-label={`${title} steps`} aria-busy={isLoading}>
          {stepsWithDynamicCounts.map((step) => (
            <li key={step.id} className={styles.stepItem}>
              <div className={styles.stepDot}>
                <span className={styles.stepDotNumber}>{step.id}</span>
              </div>

              <span className={styles.stepLabel}>{step.label}</span>

              {step.items.length > 0 && (
                <ul className={styles.itemList}>
                  {step.items.map((item) => {
                    return (
                      <li
                        key={item.label}
                        className={classNames(styles.itemRow, {
                          [styles.disabled]: item.disabled,
                          [styles.clickable]: Boolean(item.path) && !item.disabled,
                        })}
                        onClick={() => handleItemClick(item)}>
                        <span className={styles.itemLabel}>{item.label}</span>
                        <span className={styles.countBadge}>
                          {renderCountValue(item.count, getItemCountLoading(step, item))}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              )}
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
};
